/**
 * Placeholders shaped like the thing that is coming.
 *
 * ## Why not a spinner
 *
 * A centred spinner says "something is happening" and nothing else. It gives
 * no idea what is arriving or how much of it, and — the part that actually
 * hurts — it occupies almost no height, so the page is short while it spins
 * and tall the instant data lands. Everything below jumps, including whatever
 * somebody was about to click.
 *
 * On Contracts that was visible in its worst form: a spinner in an empty
 * region with the footer floating underneath it, which reads as a page that
 * has finished loading and found nothing.
 *
 * A skeleton answers both. It says a table is coming, roughly this big, and
 * it holds the space so nothing moves when the real rows replace it.
 *
 * ## Why they do not shimmer aggressively
 *
 * `animate-pulse` and no more. A skeleton is furniture, not an event; a
 * sweeping gradient across eight rows is the same mistake as the animated
 * canvas behind a table, at a smaller scale. `motion-reduce` stops it
 * entirely, and a still grey block still holds the space, which is most of
 * the job.
 */

/** One grey block. Width is a class so callers can vary the ragged edge. */
export function SkeletonLine({ className = 'w-full' }: { className?: string }) {
  return (
    <span
      className={`block h-4 rounded bg-muted motion-safe:animate-pulse ${className}`}
      aria-hidden="true"
    />
  );
}

/**
 * Rows for a table that is on its way.
 *
 * `columns` and `rows` are guesses at what is coming, and being roughly right
 * is the whole point — a skeleton the wrong size moves the page anyway, which
 * is the thing it exists to prevent.
 */
export function TableSkeleton({
  columns = 4,
  rows = 6,
  label = 'Loading',
}: {
  columns?: number;
  rows?: number;
  label?: string;
}) {
  return (
    <div
      className="overflow-hidden rounded-2xl border border-border"
      role="status"
      aria-label={label}
    >
      <div className="flex gap-4 border-b border-border bg-muted/50 px-4 py-3">
        {Array.from({ length: columns }).map((_, i) => (
          <SkeletonLine key={i} className={i === 0 ? 'w-40' : 'w-24'} />
        ))}
      </div>

      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-4 border-b border-border px-4 py-3 last:border-0">
          {Array.from({ length: columns }).map((_, c) => (
            <SkeletonLine
              key={c}
              /*
               * Varied widths, because a grid of identical bars reads as a
               * rendering fault rather than as text that has not arrived.
               */
              className={c === 0 ? 'w-48' : c % 2 === 0 ? 'w-20' : 'w-28'}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Cards for a board or a grid of panels. */
export function CardSkeleton({
  count = 3,
  label = 'Loading',
}: {
  count?: number;
  label?: string;
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" role="status" aria-label={label}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-2xl border border-border p-4">
          <SkeletonLine className="w-32" />
          <span className="mt-3 block space-y-2">
            <SkeletonLine className="w-full" />
            <SkeletonLine className="w-4/5" />
          </span>
        </div>
      ))}
    </div>
  );
}
